// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * RandomEventManager - Consolidated event system
 * 
 * Handles all event generation, dispatch, and completion.
 * Replaces both EventTypeManager and the old RandomEventManager.
 * 
 * Simple state machine:
 * - Default: OFF
 * - Start warp: ON
 * - Stop warp: OFF
 * - During event: PAUSED
 */

import GameConfig from '../../config/GameConfig';
import STORAGE_KEYS from '../system/STORAGE_KEYS';

class RandomEventManagerClass {
  constructor() {
    this.isActive = false;
    this.eventTimer = null;
    this.listeners = {};
    this.currentEvent = null;
    this.generateRandomEvent = null;
  }

  /**
   * Add event listener
   */
  addEventListener(eventType, callback) {
    if (!this.listeners[eventType]) {
      this.listeners[eventType] = [];
    }
    this.listeners[eventType].push(callback);
  }

  /**
   * Remove event listener
   */
  removeEventListener(eventType, callback) {
    if (this.listeners[eventType]) {
      this.listeners[eventType] = this.listeners[eventType].filter(cb => cb !== callback);
    }
  }

  /**
   * Emit event to all listeners
   */
  emit(eventType, data) {
    if (this.listeners[eventType]) {
      this.listeners[eventType].forEach(callback => callback(data));
    }
  }

  /**
   * Start event generation (called when warp starts)
   */
  start() {
    if (this.isActive) {
      return;
    }
    
    this.isActive = true;
    this.scheduleNextEvent();
    this.emit('started');
  }

  /**
   * Stop event generation (called when warp stops)
   */
  stop() {
    if (!this.isActive) {
      return;
    }
    
    this.isActive = false;
    this.clearTimer();
    this.emit('stopped');
  }

  /**
   * Pause event generation (called when event is active)
   */
  pause() {
    if (!this.isActive) {
      return;
    }
    
    this.clearTimer();
    this.emit('paused');
  }

  /**
   * Resume event generation (called when event completes)
   */
  resume() {
    if (!this.isActive) {
      return;
    }
    
    this.scheduleNextEvent();
    this.emit('resumed');
  }

  /**
   * Clear the event timer
   */
  clearTimer() {
    if (this.eventTimer) {
      clearTimeout(this.eventTimer);
      this.eventTimer = null;
    }
  }

  /**
   * Schedule the next event check
   */
  scheduleNextEvent() {
    if (!this.isActive) {
      return;
    }
    
    const config = GameConfig.events.warpEvents;
    const eventDelay = Math.random() * (config.maxDelay - config.minDelay) + config.minDelay;
    
    this.eventTimer = setTimeout(() => {
      this.checkGenerateEvent();
    }, eventDelay);
  }

  /**
   * Check if we should generate an event
   */
  checkGenerateEvent() {
    if (!this.isActive) {
      return;
    }
    
    // Don't generate if there's already an active event
    if (this.currentEvent) {
      this.scheduleNextEvent(); // Schedule next check
      return;
    }
    
    // Random chance to generate event
    const config = GameConfig.events.warpEvents;
    let generationChance = config.generationChance;
    
    // Apply cloak and jamming modifiers
    const cloakActive = localStorage.getItem(STORAGE_KEYS.CLOAK_ACTIVE) === 'true';
    const jammingActive = localStorage.getItem(STORAGE_KEYS.JAMMING_ACTIVE) === 'true';
    
    if (cloakActive && jammingActive) {
      // Both active: 90% reduction (0.75 + 0.50 - 0.35 overlap = 0.90)
      generationChance *= 0.10;
    } else if (cloakActive) {
      // Cloak only: 75% reduction
      generationChance *= 0.25;
    } else if (jammingActive) {
      // Jamming only: 50% reduction
      generationChance *= 0.50;
    }
    
    const randomRoll = Math.random();
    
    if (randomRoll < generationChance) {
      if (this.generateRandomEvent) {
        const newEvent = this.generateRandomEvent();
        this.setCurrentEvent(newEvent);
        this.pause(); // Pause further events until this one completes
      } else {
        console.warn('[RandomEventManager] No event generator function set');
        this.scheduleNextEvent(); // Schedule next check
      }
    } else {
      this.scheduleNextEvent(); // Schedule next check
    }
  }

  /**
   * Set the current active event
   */
  setCurrentEvent(event) {
    this.currentEvent = event;
    if (event) {
      this.emit('eventGenerated', event);
    } else {
      this.emit('eventCleared');
    }
  }

  /**
   * Clear the current event and resume generation
   */
  clearCurrentEvent() {
    this.setCurrentEvent(null);
    
    // Resume event generation if we're still active
    if (this.isActive) {
      this.resume();
    }
  }

  /**
   * Set the event generator function
   */
  setEventGenerator(generatorFn) {
    this.generateRandomEvent = generatorFn;
  }

  /**
   * Get current state
   */
  getState() {
    return {
      isActive: this.isActive,
      currentEvent: this.currentEvent
    };
  }

  /**
   * Reset the manager
   */
  reset() {
    this.stop();
    this.currentEvent = null;
  }
}

// Create singleton instance
const RandomEventManager = new RandomEventManagerClass();

export default RandomEventManager;
